import React, { useEffect, useState } from 'react';
import liff from '@line/liff';
import { api } from '../api';
import { useNavigate } from 'react-router-dom';
import { Loader2, Zap } from 'lucide-react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth, db, liffId } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import AuthLayout from '../components/AuthLayout';
import Input from '../components/ui/Input';
import Logo from '../components/Logo';
import { useToast } from '../contexts/ToastContext';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLineSigningIn, setIsLineSigningIn] = useState(false);

  // LINEアプリ内で開いた場合は、連携済みのLINEアカウントでそのままログインする
  useEffect(() => {
    if (!liffId || !/Line\//i.test(navigator.userAgent)) return;
    (async () => {
      setIsLineSigningIn(true);
      try {
        await liff.init({ liffId });
        if (!liff.isLoggedIn()) { liff.login({ redirectUri: window.location.href }); return; }
        const accessToken = liff.getAccessToken();
        if (accessToken && await api.lineSignIn(accessToken)) { navigate('/user/shifts', { replace: true }); return; }
        showToast('このLINEアカウントは、まだシフトメイトと連携されていません。招待URLから連携してください。', 'info');
      } catch (e) {
        console.error('LINE sign-in error:', e);
      } finally {
        setIsLineSigningIn(false);
      }
    })();
  }, []);

  const handleLogin = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!email || !password) return;
    
    setIsLoading(true);
    try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        if (!userCredential.user.emailVerified) {
            showToast('メール認証が完了していません。', 'info');
            navigate('/verification');
            return;
        }
        showToast('ログインしました', 'success');
        const staffDoc = await getDoc(doc(db, 'staff', userCredential.user.uid));
        const role = staffDoc.exists() ? staffDoc.data()?.role : 'staff';
        if (role === 'system_admin' || role === 'store_manager') {
            navigate('/admin/shifts');
        } else {
            navigate('/user/shifts');
        }
    } catch {
        showToast('ログインに失敗しました。', 'error');
    } finally { setIsLoading(false); }
  };

  const handleEasyLogin = async () => {
    // 開発環境のみテスト認証情報を使用
    if (!import.meta.env.DEV) {
      showToast('この機能は開発環境でのみ利用できます。', 'error');
      return;
    }
    const testEmail = import.meta.env.VITE_TEST_EMAIL || '';
    const testPassword = import.meta.env.VITE_TEST_PASSWORD || '';
    if (!testEmail || !testPassword) {
      showToast('テスト認証情報が設定されていません。', 'error');
      return;
    }
    setEmail(testEmail);
    setPassword(testPassword);

    setIsLoading(true);
    try {
        const cred = await signInWithEmailAndPassword(auth, testEmail, testPassword);
        showToast('かんたんログインしました', 'success');
        const staffDoc = await getDoc(doc(db, 'staff', cred.user.uid));
        const role = staffDoc.exists() ? staffDoc.data()?.role : 'staff';
        if (role === 'system_admin' || role === 'store_manager') {
            navigate('/admin/shifts');
        } else {
            navigate('/user/shifts');
        }
    } catch {
        showToast('ログインに失敗しました。', 'error');
    } finally { setIsLoading(false); }
  };

  if (isLineSigningIn) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-white">
        <Loader2 className="animate-spin text-blue-600" size={32} />
        <p className="text-sm text-slate-500 font-bold">LINEでログインしています。</p>
      </div>
    );
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-sm mx-auto">
          <div className="flex justify-center mb-12 lg:hidden">
              <Logo size="lg" />
          </div>

          <h1 className="text-2xl font-bold text-slate-800 mb-10 text-center">ログイン</h1>
          
          <form onSubmit={handleLogin}>
            <Input 
              label="メールアドレス" 
              type="email" 
              placeholder="sample@email.com"
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
            />
            <Input 
              label="パスワード" 
              type="password" 
              placeholder="パスワードを入力"
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
            />
            
            <div className="text-right mb-6 -mt-2">
                <span 
                    onClick={() => navigate('/forgot-password')}
                    className="text-xs text-blue-500 font-bold cursor-pointer hover:text-blue-700 transition-colors"
                >
                    パスワードを忘れた方はこちら
                </span>
            </div>

            <div className="space-y-3">
                <button 
                    type="submit" 
                    disabled={isLoading} 
                    className="w-full py-4 bg-blue-600 text-white font-bold rounded-[4px] shadow-md hover:bg-blue-700 transition-all active:scale-95 flex items-center justify-center disabled:bg-slate-400 disabled:cursor-not-allowed"
                >
                    {isLoading ? <Loader2 className="animate-spin" size={20} /> : 'ログイン'}
                </button>

                {import.meta.env.DEV && (
                <button
                    type="button"
                    onClick={handleEasyLogin}
                    disabled={isLoading}
                    className="w-full py-3 bg-slate-100 text-slate-600 font-bold rounded-[4px] border border-slate-200 hover:bg-slate-200 transition-all flex items-center justify-center text-sm gap-2"
                >
                    {isLoading && email.includes(import.meta.env.VITE_TEST_EMAIL?.split('@')[0] || '') ? (
                        <Loader2 className="animate-spin" size={18} />
                    ) : (
                        <>
                            <Zap size={16} className="text-amber-500 fill-amber-500" />
                            管理者アカウントでログイン
                        </>
                    )}
                </button>
                )}
            </div>
          </form>

          <div className="mt-12 border-t border-gray-200 pt-6 text-center">
            <span className="text-sm text-slate-600">はじめてご利用の方は</span>
            <span 
                onClick={() => navigate('/register')} 
                className="cursor-pointer text-sm text-blue-500 border-b border-blue-500 ml-1 hover:text-blue-700 hover:border-blue-700 pb-0.5 transition-colors font-bold"
            >
                こちら
            </span>
          </div>
      </div>
    </AuthLayout>
  );
};

export default LoginPage;